/* eslint-disable react/react-in-jsx-scope */
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ANNOTATION_SOURCE_LOCATION } from '@backstage/catalog-model';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import {
  isAgentEntityV1alpha1,
  isMCPEntityV1alpha1,
  isSkillEntityV1alpha1,
} from '../../kinds/guards';
import { EntityRefLink } from '@backstage/plugin-catalog-react';

export function AgentAboutCard() {
  const { entity } = useEntity();
  if (!isAgentEntityV1alpha1(entity)) {
    return null;
  }
  const a = entity;
  return (
    <Grid item md={6}>
      <InfoCard title="Agent">
        <Typography variant="body2">Type: {a.spec.type}</Typography>
        <Typography variant="body2">
          Lifecycle: {a.spec.lifecycle}
        </Typography>
        {a.spec.model && (
          <Typography variant="body2">Model: {a.spec.model}</Typography>
        )}
        {a.spec.platform && (
          <Typography variant="body2">Platform: {a.spec.platform}</Typography>
        )}
        {a.spec.trigger && (
          <Typography variant="body2">Trigger: {a.spec.trigger}</Typography>
        )}
        {a.spec.schedule && (
          <Typography variant="body2">Schedule: {a.spec.schedule}</Typography>
        )}
      </InfoCard>
    </Grid>
  );
}

export function AgentSkillsCard() {
  const { entity } = useEntity();
  if (!isAgentEntityV1alpha1(entity)) {
    return null;
  }
  const refs = entity.spec.skills ?? [];
  return (
    <Grid item md={6}>
      <InfoCard title="Skills">
        {refs.length === 0 ? (
          <Typography variant="body2">None</Typography>
        ) : (
          refs.map(r => (
            <Typography key={r} variant="body2" component="div">
              <EntityRefLink entityRef={r} defaultKind="skill" />
            </Typography>
          ))
        )}
      </InfoCard>
    </Grid>
  );
}

export function AgentMcpsCard() {
  const { entity } = useEntity();
  if (!isAgentEntityV1alpha1(entity)) {
    return null;
  }
  const refs = entity.spec.mcps ?? [];
  return (
    <Grid item md={6}>
      <InfoCard title="MCPs">
        {refs.length === 0 ? (
          <Typography variant="body2">None</Typography>
        ) : (
          refs.map(r => (
            <Typography key={r} variant="body2">
              <EntityRefLink entityRef={r} defaultKind="mcp" />
            </Typography>
          ))
        )}
      </InfoCard>
    </Grid>
  );
}

export function AgentSourceCard() {
  const { entity } = useEntity();
  if (!isAgentEntityV1alpha1(entity) || entity.spec.type !== 'markdown') {
    return null;
  }
  const loc =
    entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION] ?? '';
  const path = entity.spec.sourcePath;
  return (
    <Grid item md={6}>
      <InfoCard title="Source">
        {path && <Typography variant="body2">Path: {path}</Typography>}
        {loc && (
          <a href={loc.replace(/^url:/, '')} target="_blank" rel="noopener noreferrer">
            Open in repo
          </a>
        )}
      </InfoCard>
    </Grid>
  );
}

export function AgentRuntimeCard() {
  const { entity } = useEntity();
  if (!isAgentEntityV1alpha1(entity) || entity.spec.type !== 'custom-runtime') {
    return null;
  }
  const s = entity.spec;
  return (
    <Grid item md={6}>
      <InfoCard title="Runtime">
        <Typography variant="body2">Runtime: {s.runtime ?? '—'}</Typography>
        <Typography variant="body2">
          Language: {s.language ?? '—'}
        </Typography>
        <Typography variant="body2">
          Framework: {s.framework ?? '—'}
        </Typography>
        {(s.consumesApis ?? []).map(a => (
          <Typography key={a} variant="body2">
            Consumes: <EntityRefLink entityRef={a} defaultKind="api" />
          </Typography>
        ))}
      </InfoCard>
    </Grid>
  );
}

export function SkillAboutCard() {
  const { entity } = useEntity();
  if (!isSkillEntityV1alpha1(entity)) {
    return null;
  }
  const s = entity.spec;
  const loc =
    entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION] ?? '';
  const href = loc.replace(/^url:/, '');
  return (
    <Grid item md={6}>
      <InfoCard title="Skill">
        <Typography variant="body2">Type: {s.type}</Typography>
        <Typography variant="body2">Lifecycle: {s.lifecycle}</Typography>
        {href ? (
          <Typography variant="body2">
            <a href={href} target="_blank" rel="noopener noreferrer">
              Skill file
            </a>
          </Typography>
        ) : null}
      </InfoCard>
    </Grid>
  );
}

export function SkillToolsCard() {
  const { entity } = useEntity();
  if (!isSkillEntityV1alpha1(entity)) {
    return null;
  }
  const tools = entity.spec.tools ?? entity.spec.allowedTools ?? [];
  return (
    <Grid item md={6}>
      <InfoCard title="Tools">
        {tools.length === 0 ? (
          <Typography variant="body2">None listed</Typography>
        ) : (
          tools.map(t => (
            <Chip key={t} style={{ margin: 4 }} label={t} />
          ))
        )}
      </InfoCard>
    </Grid>
  );
}

export function SkillKnowledgeCard() {
  const { entity } = useEntity();
  if (!isSkillEntityV1alpha1(entity)) {
    return null;
  }
  const k = entity.spec.knowledge ?? [];
  return (
    <Grid item md={6}>
      <InfoCard title="Knowledge">
        {k.length === 0 ? (
          <Typography variant="body2">None</Typography>
        ) : (
          k.map(r => (
            <Typography key={r} variant="body2">
              <EntityRefLink entityRef={r} defaultKind="resource" />
            </Typography>
          ))
        )}
      </InfoCard>
    </Grid>
  );
}

export function SkillDependenciesCard() {
  const { entity } = useEntity();
  if (!isSkillEntityV1alpha1(entity)) {
    return null;
  }
  const ds = entity.spec.dependsOnSkills ?? [];
  const ms = entity.spec.mcps ?? [];
  // drop sections that are empty
  const hasDependencies = ds.length > 0 || ms.length > 0;
  if (!hasDependencies) {
    return null;
  }
  return (
    <Grid item md={6}>
      <InfoCard title="Dependencies">
        {ds.length > 0 && (
          <Typography variant="subtitle2">dependsOnSkills</Typography>
        )}
        {ds.length > 0 && ds.map(r => (
          <Typography key={r} variant="body2">
            <EntityRefLink entityRef={r} defaultKind="skill" />
          </Typography>
        ))}
        {ms.length > 0 && (
          <Typography variant="subtitle2">MCPs</Typography>
        )}
        {ms.length > 0 && ms.map(r => (
          <Typography key={r} variant="body2">
            <EntityRefLink entityRef={r} defaultKind="mcp" />
          </Typography>
        ))}
      </InfoCard>
    </Grid>
  );
}

export function McpAboutCard() {
  const { entity } = useEntity();
  if (!isMCPEntityV1alpha1(entity)) {
    return null;
  }
  const s = entity.spec;
  return (
    <Grid item md={6}>
      <InfoCard title="MCP">
        <Typography variant="body2">Protocol: {s.protocol}</Typography>
        <Typography variant="body2">Lifecycle: {s.lifecycle}</Typography>
        {s.endpoint && (
          <Typography variant="body2">Endpoint: {s.endpoint}</Typography>
        )}
        {s.command && (
          <Typography variant="body2">
            Command: {s.command} {(s.args ?? []).join(' ')}
          </Typography>
        )}
      </InfoCard>
    </Grid>
  );
}

export function McpAuthCard() {
  const { entity } = useEntity();
  if (!isMCPEntityV1alpha1(entity)) {
    return null;
  }
  const rows = entity.spec.auth ?? [];
  if (!rows.length) {
    return null;
  }
  return (
    <Grid item md={6}>
      <InfoCard title="Authentication">
        {rows.map((a, i) => (
          <Typography key={i} variant="body2">
            {a.type}
            {a.tokenUrl ? ` — token: ${a.tokenUrl}` : ''}
            {a.audience ? ` — aud: ${a.audience}` : ''}
          </Typography>
        ))}
      </InfoCard>
    </Grid>
  );
}

export function McpAssetsCard() {
  const { entity } = useEntity();
  if (!isMCPEntityV1alpha1(entity)) {
    return null;
  }
  const s = entity.spec;
  // put empty asset categories in italics
  const hasTools = (s.providesTools?.length ?? 0) > 0;
  const hasResources = (s.providesResources?.length ?? 0) > 0;
  const hasPrompts = (s.providesPrompts?.length ?? 0) > 0;
  return (
    <Grid item md={12}>
      <InfoCard title="Assets">
        {hasTools ? <Typography variant="h6">Tools</Typography> : <Typography variant="h6"><em>No tools available</em></Typography>}
        {(s.providesTools ?? []).map(t => (
          <Chip key={t} style={{ margin: 4 }} label={t} />
        ))}
        {hasResources ? <Typography variant="h6">Resources</Typography> : <Typography variant="h6"><em>No resources available</em></Typography>}
        {(s.providesResources ?? []).map(t => (
          <Chip key={t} style={{ margin: 4 }} label={t} />
        ))}
        {hasPrompts ? <Typography variant="h6">Prompts</Typography> : <Typography variant="h6"><em>No prompts available</em></Typography>}
        {(s.providesPrompts ?? []).map(t => (
          <Chip key={t} style={{ margin: 4 }} label={t} />
        ))}
      </InfoCard>
    </Grid>
  );
}

export function McpDependenciesCard() {
  const { entity } = useEntity();
  if (!isMCPEntityV1alpha1(entity)) {
    return null;
  }
  const d = entity.spec.dependsOn ?? [];
  const c = entity.spec.consumesApis ?? [];
  return (
    <Grid item md={12}>
      <InfoCard title="Dependencies">
        {d.map(r => (
          <Typography key={r} variant="body2">
            dependsOn: <EntityRefLink entityRef={r} />
          </Typography>
        ))}
        {c.map(r => (
          <Typography key={r} variant="body2">
            consumesApis: <EntityRefLink entityRef={r} defaultKind="api" />
          </Typography>
        ))}
      </InfoCard>
    </Grid>
  );
}
